import {
  ChainLinkIcon,
  EditIcon,
  MailFilledIcon,
  OccupantsIcon,
  PhoneFilledIcon,
  TimeFilledIcon,
} from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { BookablePerson } from '@sales-app/types/BookablePerson'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import { EditLeadForm } from '@sales-app/views/Workspace/PersonSummary/EditLead/EditLeadForm'
import { Tooltip } from '@shared/components'
import React, { FC } from 'react'

import { ReactComponent as FlagIcon } from '@/assets/icons/flag.svg'
import { BottomSheet, History } from '@/components'
import { useAlertContext } from '@/context'
import { RecordType } from '@/graphql/queries/fieldHistoryLogs/fieldHistoryLogs.queries'
import { useToggle } from '@/hooks'

const ADMIN_URL = process.env.ADMIN_URL

type Props = {
  bookablePerson: BookablePerson
  hasDuplicates?: boolean
  isDuplicatesLoading?: boolean
  refetch: () => void
}

export const BookablePersonHeader: FC<Props> = ({ bookablePerson, isDuplicatesLoading, hasDuplicates, refetch }) => {
  const { setAlert } = useAlertContext()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { isShown: leadHistoryModalShown, toggle: leadHistoryModalToggle } = useToggle()

  const handleEdit = () => {
    if (!isDuplicatesLoading) {
      if (hasDuplicates) {
        setAlert('Duplicates needs to be reviewed first', 'info')
      } else {
        modalToggle()
      }
    }
  }

  return (
    <div className="flex w-full flex-col">
      <div className="flex h-full w-full">
        <>
          {bookablePerson && (
            <div className="flex w-full flex-col">
              <div className="mb-4 flex w-full justify-between pt-4 md:items-center">
                <div className="flex items-center">
                  <div className="flex mr-4">
                    <StyledHeader className="text-header-lg capitalize">{bookablePerson.fullName || ''}</StyledHeader>
                  </div>

                  {bookablePerson.bookablePersonType === BookablePersonType.LEAD && (
                    <div className="flex flex-row">
                      {handleEdit && (
                        <div className="flex">
                          <Tooltip text="Edit the lead">
                            <EditIcon className="w-5 cursor-pointer ml-8 text-green-300" onClick={handleEdit} />
                          </Tooltip>
                        </div>
                      )}
                      {leadHistoryModalToggle && (
                        <div className="flex items-center">
                          <Tooltip text="View the lead history">
                            <TimeFilledIcon
                              className="w-5 cursor-pointer ml-8 icon-tertiary"
                              onClick={leadHistoryModalToggle}
                            />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  )}

                  {hasDuplicates && <FlagIcon className="ml-2 text-red-300" />}
                </div>
              </div>
              <div className="flex flex-col w-full mb-4 md:flex-row">
                <div className="flex mr-8">
                  <OccupantsIcon className="w-6 mr-2" height={28} width={28} />
                  <span>{bookablePerson.bookablePersonType}</span>
                </div>
                <div className="flex mr-8 underline">
                  <a href={`mailto: ${bookablePerson.email}`} rel="noreferrer" target="_blank">
                    <div className="flex flex-row">
                      <Tooltip text="Send an email">
                        <MailFilledIcon className="w-6 mr-2" />
                      </Tooltip>
                      <div className="flex">{bookablePerson.email}</div>
                    </div>
                  </a>
                </div>
                <div className="flex underline mr-8">
                  <a href={`dialpad://${bookablePerson.phone}`} rel="noreferrer" target="_blank">
                    <div className="flex flex-row">
                      <div>
                        <Tooltip text="Call with Dialpad">
                          <PhoneFilledIcon className="w-6 mr-2" height={28} width={28} />
                        </Tooltip>
                      </div>
                      <div>{bookablePerson.phone}</div>
                    </div>
                  </a>
                </div>
                {bookablePerson.bookablePersonType === BookablePersonType.MEMBER && (
                  <div className="flex">
                    <a
                      href={`${ADMIN_URL}/admin/users/${bookablePerson.bookablePersonId}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <div className="flex flex-row">
                        <div>
                          <ChainLinkIcon className="w-6 mr-2" height={28} width={28} />
                        </div>
                        <div className="whitespace-nowrap">
                          <span className="underline">View User in Admin</span>
                        </div>
                      </div>
                    </a>
                  </div>
                )}
                {bookablePerson.bookablePersonType === BookablePersonType.LEAD && (
                  <>
                    <BottomSheet isVisible={modalShown} mobileFullHeight={true} onClose={modalToggle}>
                      <EditLeadForm lead={bookablePerson} refetchOnSuccess={refetch} toggle={modalToggle} />
                    </BottomSheet>
                    <BottomSheet
                      isVisible={leadHistoryModalShown}
                      mobileFullHeight={true}
                      onClose={leadHistoryModalToggle}
                    >
                      <History recordId={bookablePerson.bookablePersonId} recordType={RecordType.LEADS} />
                    </BottomSheet>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  )
}

// Keep in sync with Tailwind's `md` breakpoint (tailwind.config.js default: 768px)
const MD_BREAKPOINT = '768px'

const StyledHeader = styled.p`
  @media (max-width: ${MD_BREAKPOINT}) {
    display: -webkit-box;
    max-width: 100%;
    height: 3rem;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 120%;
  }
`
